/**
 * "/admin" — WHO OWNS A BOARD ONCE IT REACHES SHOOTING (F-133).
 *
 * Approving a storyboard moves the board to Shooting on its own and writes the
 * stage task; nothing asks the approver who should own it, because the answer
 * is the same people on every board. This is where that answer is set — once,
 * by the project owner, instead of on every approval.
 *
 * Empty is a real setting, not an unset one: with nobody picked, whoever
 * approves the storyboard owns the shoot. The section says so rather than
 * leaving a blank list to be read as "broken".
 *
 * Saving is explicit (not save-on-toggle) because this is workspace-wide: the
 * next approval on any board hands the shoot to whoever is listed here.
 */
import type { TeamMember } from "@kola/shared";
import { useEffect, useState } from "react";

import { fetchShootingOwners, saveShootingOwners } from "../../core/api.js";
import { assigneesFrom, RosterPicker } from "../../ui/RosterPicker.js";
import { showToast } from "../../ui/toast.js";

export function ShootingOwnersSection() {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedBy, setSavedBy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchShootingOwners()
      .then((owners) => {
        if (cancelled) return;
        setSelected(new Set(owners.assignees.map((a) => a.uid)));
        setSavedAt(owners.updatedAt);
        setSavedBy(owners.updatedBy);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Could not load the setting.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (uid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // `assigneesFrom` reads the roster, so a uid whose member has since left
      // the team simply drops out of the saved list rather than being stored
      // as a name-less id nobody can resolve.
      const saved = await saveShootingOwners(assigneesFrom(team, selected));
      setSavedAt(saved.updatedAt);
      setSavedBy(saved.updatedBy);
      showToast(
        saved.assignees.length === 0
          ? "Saved — whoever approves a storyboard will own the shoot"
          : "Shooting owners saved",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the setting.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="admin-section">
      <div className="admin-section-head">
        <h2>Shooting owners</h2>
        <span className="admin-card-hint">
          Approving a storyboard moves the board to Shooting and creates its task. These are
          the people that task goes to. Pick nobody and it goes to whoever approved it.
        </span>
      </div>

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : (
        <RosterPicker
          selected={selected}
          onToggle={toggle}
          onLoaded={setTeam}
          disabled={saving}
        />
      )}

      {error !== null && <div className="error-note">{error}</div>}

      <div className="shooting-owners-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void save()}
          disabled={loading || saving}
        >
          {saving ? "Saving…" : "Save shooting owners"}
        </button>
        {savedAt !== null && (
          <span className="admin-card-hint">
            Last saved {new Date(savedAt).toLocaleDateString()}
            {savedBy !== null && savedBy !== "" ? ` by ${savedBy}` : ""}
          </span>
        )}
      </div>
    </section>
  );
}
